/** Files page: pinned first, then newest first. Admins upload, edit, pin and delete. */
import { api } from "../api";
import { isAdmin, isStaff, on } from "../state";
import { $, I, closeSheet, edHead, esc, fmtDate, openSheet, sized, toast } from "../ui";
import { ACCEPT, fmtSize } from "./feedback";

type FileView = { id: number; name: string; filename: string; type: string; size: number; pa: boolean; sa: boolean; pinned: boolean; createdAt: number };
const FL: { list: FileView[] | null; loading: boolean } = { list: null, loading: false };
const MAX_MB = 25;

export const resetFiles = () => (FL.list = null);

async function load() {
  if (FL.loading) return;
  FL.loading = true;
  try {
    FL.list = await api<FileView[]>("/api/files");
  } catch (e) {
    toast((e as Error).message);
  }
  FL.loading = false;
  const box = $("#flist");
  if (box) box.innerHTML = body();
}

const who = (f: FileView) => (f.pa && f.sa ? "PAs and SAs" : f.pa ? "PAs" : "SAs");

function body() {
  if (!FL.list) return '<p class="muted">Loading…</p>';
  if (!FL.list.length) return `<p class="muted">${isAdmin() ? "No files yet. Upload one to share it with ambassadors." : "No files yet."}</p>`;
  return `<div class="stack" style="gap:8px">${FL.list
    .map(
      (f) => `<div class="card row" style="gap:12px;padding:12px 14px">
      <span style="color:var(--primary);flex:none">${sized(I.file, 26)}</span>
      <div style="min-width:0;flex:1"><a href="/api/files/${f.id}/download" target="_blank" rel="noopener" style="font-weight:700">${esc(f.name)}</a>${f.pinned ? ' <span class="chip blue">📌 Pinned</span>' : ""}
        <div class="muted small">${esc(f.filename)} · ${fmtSize(f.size)} · ${fmtDate(f.createdAt)}${isStaff() ? ` · For ${who(f)}` : ""}</div></div>
      ${isAdmin() ? `<button class="btn ghost sm" style="flex:none" data-act="file-edit" data-id="${f.id}">Edit</button>` : ""}</div>`,
    )
    .join("")}</div>`;
}

export function filesView() {
  if (!FL.list) void load();
  return `<div class="stack">${isAdmin() ? `<div class="row"><button class="btn sm" style="margin-left:auto" data-act="file-new">${sized(I.plus, 14)} Upload File</button></div>` : ""}<div id="flist">${body()}</div></div>`;
}

const chk = (id: string, label: string, on: boolean) =>
  `<label class="row" style="gap:8px;cursor:pointer"><input type="checkbox" id="${id}" ${on ? "checked" : ""} style="width:18px;height:18px"> ${label}</label>`;

function editor(f?: FileView) {
  openSheet(`${edHead(f ? "Edit File" : "Upload File")}<div class="edbody">
    <section class="edsec"><h3>File</h3>
      ${f ? `<div class="small">${esc(f.filename)} · ${fmtSize(f.size)} · uploaded ${fmtDate(f.createdAt)}</div>` : `<input type="file" id="ffile" accept="${ACCEPT}"><div class="muted small">PDF, photo, Office or text file, up to ${MAX_MB} MB.</div>`}
      <div><label class="fld" for="fname">Display Name</label><input type="text" id="fname" maxlength="100" value="${esc(f?.name || "")}" placeholder="${f ? "" : "Uses the file name if left blank"}"></div></section>
    <section class="edsec"><h3>Who Can See It</h3><div class="edchecks">${chk("fpa", "Parent Ambassadors", f ? f.pa : true)}${chk("fsa", "Student Ambassadors", f ? f.sa : true)}</div>
      <p class="muted small" style="margin:0">Admissions and admins see every file.</p></section>
    <section class="edsec"><h3>Order</h3>${chk("fpin", "Pin to the Top", !!f?.pinned)}</section>
    <div class="err" id="err" role="alert"></div></div>
    <div class="edfoot"><button class="btn" data-act="file-save" ${f ? `data-id="${f.id}"` : ""}>${f ? "Save" : "Upload"}</button>${
      f ? `<button class="btn ghost" data-act="file-del" data-id="${f.id}">Delete</button>` : ""
    }<button class="btn ghost" data-act="close-sheet">Cancel</button></div>`);
}

const checked = (id: string) => ($(`#${id}`) as HTMLInputElement).checked;

on({
  "file-new": () => editor(),
  "file-edit": (el) => editor(FL.list?.find((f) => f.id === Number(el.dataset.id))),
  "file-save": async (el) => {
    const err = $("#err")!;
    const btn = el as HTMLButtonElement;
    const name = ($("#fname") as HTMLInputElement).value;
    if (!checked("fpa") && !checked("fsa")) return (err.textContent = "Pick who can see it: Parent Ambassadors, Student Ambassadors or both.");
    btn.disabled = true;
    try {
      if (el.dataset.id) {
        await api(`/api/files/${el.dataset.id}`, { method: "PATCH", body: { name, pa: checked("fpa"), sa: checked("fsa"), pinned: checked("fpin") } });
      } else {
        const file = ($("#ffile") as HTMLInputElement).files?.[0];
        if (!file) throw new Error("Choose a file to upload.");
        if (file.size > MAX_MB * 1048576) throw new Error(`${file.name} is too big. The limit is ${MAX_MB} MB.`);
        const fd = new FormData();
        fd.set("file", file);
        fd.set("name", name);
        fd.set("pa", checked("fpa") ? "1" : "0");
        fd.set("sa", checked("fsa") ? "1" : "0");
        fd.set("pinned", checked("fpin") ? "1" : "0");
        await api("/api/files", { form: fd });
      }
      closeSheet();
      toast(el.dataset.id ? "Saved." : "Uploaded.");
      FL.list = null;
      $("#flist") && ($("#flist")!.innerHTML = body());
      void load();
    } catch (e) {
      err.textContent = (e as Error).message;
      btn.disabled = false;
    }
  },
  "file-del": async (el) => {
    const btn = el as HTMLButtonElement;
    if (btn.dataset.sure !== "1") {
      btn.dataset.sure = "1";
      btn.textContent = "Tap Again to Delete";
      return;
    }
    try {
      await api(`/api/files/${el.dataset.id}`, { method: "DELETE" });
      closeSheet();
      toast("Deleted.");
      FL.list = FL.list?.filter((f) => f.id !== Number(el.dataset.id)) || null;
      $("#flist") && ($("#flist")!.innerHTML = body());
    } catch (e) {
      toast((e as Error).message);
    }
  },
});
